import { useState, useEffect } from 'react';
import { sysConfigApiService } from '../services/sysConfigApiService';
import { setTermBuckets, DEFAULT_TERM_BUCKETS } from '../lib/termLabel';

const HARDCODED_DEFAULTS = DEFAULT_TERM_BUCKETS;

// Module-level cache so every component shares the same fetch
let _cached: number[] | null = null;
let _promise: Promise<number[]> | null = null;

async function fetchTerms(): Promise<number[]> {
  if (_cached) return _cached;
  if (_promise) return _promise;

  _promise = sysConfigApiService.getRequestedContractTerms().then(terms => {
    _cached = terms;
    _promise = null;
    // Labelling snaps to these same buckets, and almost no component that renders a term label
    // has the list to hand — see lib/termLabel.ts. Until this lands it uses the same defaults.
    setTermBuckets(terms);
    return _cached;
  }).catch(() => {
    _promise = null;
    return [...HARDCODED_DEFAULTS];
  });

  return _promise;
}

/**
 * Returns the standard contract term lengths (months) offers are requested at
 * (sys_config.requested_contract_terms). Used to group a retailer's non-standard
 * actual term (e.g. 13 months) under the nearest one — see offerTermUtils.getNearestRequestedTerm.
 * Falls back to DEFAULT_TERM_BUCKETS until loaded / if unconfigured.
 */
export const useRequestedContractTerms = (): number[] => {
  const [terms, setTerms] = useState<number[]>(_cached ?? HARDCODED_DEFAULTS);

  useEffect(() => {
    if (_cached) return; // Already loaded — state is already correct
    fetchTerms().then(setTerms);
  }, []);

  return terms;
};
